<template>
	<Skeleton :paragraph="{ rows: 8 }" :title="false" :loading="isLoading">
		<div class="flex-wrapper">
			<div class="flex-grow"></div>
			<Button type="primary" @click="openCreateModal">{{ $t('settings.vrsSettingsForm.lsFile.addButtonLabel') }}</Button>
		</div>
		<Table
			:columns="columns"
			:data-source="tableItems"
			:pagination="false"
			:rowClassName="getRowClassName"
			row-key="id"
			size="small"
		>
			<template #bodyCell="{ column, record }">
				<template v-if="record.isSectionHeader">
					<template v-if="column.dataIndex === 'date'">
						<TypographyText type="secondary">
							{{ $t('settings.vrsSettingsForm.lsFile.firmwareDefaultSectionTitle') }}
						</TypographyText>
					</template>
				</template>
				<template v-else-if="record.isExpandToggle">
					<template v-if="column.dataIndex === 'date'">
						<div class="leap-seconds-expand-toggle-content" @click="toggleFirmwareDefaultsExpanded">
							<template v-if="!isFirmwareDefaultsExpanded">
								<DownOutlined />
								{{ $t('settings.vrsSettingsForm.lsFile.showMore') }}
								<DownOutlined />
							</template>
							<template v-else>
								<UpOutlined />
								{{ $t('settings.vrsSettingsForm.lsFile.showLess') }}
								<UpOutlined />
							</template>
						</div>
					</template>
				</template>
				<template v-else-if="column.dataIndex === 'actions'">
					<Space v-if="!record.isDefault">
						<Button type="link" size="small" @click="openEditModal(record)">
							{{ $t('settings.vrsSettingsForm.lsFile.editButtonLabel') }}
						</Button>
						<PopconfirmButton
							:title="$t('settings.vrsSettingsForm.lsFile.deleteConfirmation')"
							:ok-text="$t('settings.vrsSettingsForm.lsFile.deleteButtonLabel')"
							:cancel-text="$t('settings.vrsSettingsForm.cancelButtonLabel')"
							size="small"
							:onConfirm="() => deleteItem(record)"
						>
							{{ $t('settings.vrsSettingsForm.lsFile.deleteButtonLabel') }}
						</PopconfirmButton>
					</Space>
				</template>
			</template>
		</Table>
	</Skeleton>
	<LeapSecondsModal
		v-model="isModalVisible"
		:item="selectedItem"
		:onLeapSecondsUpdate="fetchItems"
	/>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { Button, Skeleton, Space, Table, TypographyText } from 'ant-design-vue';
import { DownOutlined, UpOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';
import { toCamelCase } from '@/api/ApiHelpers';
import Consts from '@/api/Consts';

import { showErrorMessage } from '@/components/common/errorMessage';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import LeapSecondsModal from '@/components/settings/LeapSecondsModal.vue';

const FIRMWARE_DEFAULTS_SECTION_HEADER_ID = '__firmware_defaults_header__';
const FIRMWARE_DEFAULTS_EXPAND_TOGGLE_ID = '__firmware_defaults_expand__';

const withSpanningRowCells = column => ({
	...column,
	customCell: record => {
		if (!record.isSectionHeader && !record.isExpandToggle) {
			return {};
		}
		if (column.dataIndex === 'date') {
			return { colSpan: 3 };
		}
		return { colSpan: 0 };
	},
});

export default {
	components: {
		Button, Skeleton, Space, Table, TypographyText,
		DownOutlined, UpOutlined,
		PopconfirmButton, LeapSecondsModal,
	},
	setup() {
		const isLoading = ref(true);
		const isModalVisible = ref(false);
		const isFirmwareDefaultsExpanded = ref(false);
		const items = ref([]);
		const selectedItem = ref(null);

		const columns = computed(() => Consts.leapSecondsTableColumnsComp.value.map(withSpanningRowCells));

		const tableItems = computed(() => {
			const defaultItems = items.value.filter(item => item.isDefault);
			const userItems = items.value.filter(item => !item.isDefault);
			const previewCount = Consts.leapSecondsFirmwarePreviewCount;
			const rows = [...userItems];
			if (defaultItems.length === 0) {
				return rows;
			}
			rows.push({
				id: FIRMWARE_DEFAULTS_SECTION_HEADER_ID,
				isSectionHeader: true,
			});
			if (defaultItems.length <= previewCount) {
				rows.push(...defaultItems);
				return rows;
			}
			if (isFirmwareDefaultsExpanded.value) {
				rows.push(...defaultItems);
			} else {
				rows.push(...defaultItems.slice(0, previewCount));
			}
			rows.push({
				id: FIRMWARE_DEFAULTS_EXPAND_TOGGLE_ID,
				isExpandToggle: true,
			});
			return rows;
		});

		const getRowClassName = record => {
			if (record.isSectionHeader) {
				return 'leap-seconds-section-header';
			}
			if (record.isExpandToggle) {
				return 'leap-seconds-expand-toggle';
			}
			return '';
		};

		const toggleFirmwareDefaultsExpanded = () => {
			isFirmwareDefaultsExpanded.value = !isFirmwareDefaultsExpanded.value;
		};

		const fetchItems = () => {
			isLoading.value = true;
			Api.vrsLeapSecondsGet(responseData => {
				items.value = responseData.items.map(item => toCamelCase(item));
				isLoading.value = false;
			});
		};

		onMounted(fetchItems);

		const openCreateModal = () => {
			selectedItem.value = null;
			isModalVisible.value = true;
		};

		const openEditModal = record => {
			selectedItem.value = record;
			isModalVisible.value = true;
		};

		const deleteItem = record => {
			isLoading.value = true;
			Api.vrsLeapSecondsDelete(record.id, response => {
				if (response.status === 'ok') {
					fetchItems();
					return;
				}
				showErrorMessage(response.errors);
				isLoading.value = false;
			});
		};

		return {
			isLoading,
			isModalVisible,
			isFirmwareDefaultsExpanded,
			items,
			selectedItem,
			columns,
			tableItems,
			getRowClassName,
			toggleFirmwareDefaultsExpanded,
			fetchItems,
			openCreateModal,
			openEditModal,
			deleteItem,
		};
	},
}
</script>

<style scoped>
:deep(tr.leap-seconds-section-header td),
:deep(tr.leap-seconds-expand-toggle td) {
	background-color: #fafafa;
}

.leap-seconds-expand-toggle-content {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: rgba(0, 0, 0, 0.45);
	cursor: pointer;
	user-select: none;
}

:deep(tr.leap-seconds-expand-toggle td) {
	text-align: center;
}
</style>
